import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";

// Global CSS for carousels
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Layout components
import Navbar from "./components/Users/Navbar";
import Footer from "./components/Users/Footer";
import PopupCard from "./components/Users/PopupCard";
import MaintenancePage from "./components/Users/MaintenancePage";
import MashoAIPop from "./components/Users/MashoAIPop";
import MashoAIModal from "./components/Users/MashoAIModal";

// Pages
import HomePage from "./components/Users/HomePage";
import SignUp from "./components/Users/SignUp";
import UserDetails from "./components/Users/UserDetails";
import UserProfileSection from "./components/Users/UserProfileSection";
import MashoStudioPage from "./components/Users/MashoStudioPage";
import ShoppingPage from "./components/Users/ShoppingPage";
import AboutUs from "./components/Users/AboutUs";
import TrackOrder from "./components/Users/TrackOrder";
import CartPage from "./components/Users/CartPage";
import ProductDetail from "./components/Users/ProductDetail";
import BillingPage from "./components/Users/BillingPage";
import NewArrivalPage from "./components/Users/NewArrivalPage";
import TestimonialPage from "./components/Users/TestimonialPage";
import ReturnPolicy from "./components/Users/ReturnPolicy";
import ShippingPolicy from "./components/Users/ShippingPolicy";
import PrivacyPolicy from "./components/Users/PrivacyPolicy";
import TermsAndConditions from "./components/Users/TermsAndConditions";
import OAuthCallback from "./components/Users/OAuthCallback";
import OrderSuccess from "./components/Users/OrderSuccess";
import RefundPolicy from "./components/Users/RefundPolicy";
import LogoutButton from "./components/Users/LogoutButton";
import Mashologo from "../src/assets/images/Logo for website/Desktop.png";
import PaymentCallback from "./components/Users/PaymentCallback";
import OrderFailure from "./components/Users/OrderFailure";
import ScrollToTop from "./components/Users/ScrollToTop";
import PerfumesList from "./components/Users/PerfumesList";
import AttarList from "./components/Users/AttarList";
import ComboAndGifts from "./components/Users/ComboAndGifts";
import ComboAndGiftsInfo from "./components/Users/ComboAndGiftsInfo";


// Auth0 configuration
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const auth0Config = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN,
  clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
  redirectUri: window.location.origin + "/oauth/callback",
  audience: "https://masho-api/",
  scope: "openid profile email"
};

// Create Authentication Context
const AuthContext = createContext();

// Auth Provider Component that wraps Auth0 provider
const Auth0ProviderWithHistory = ({ children }) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: auth0Config.redirectUri,
        audience: auth0Config.audience,
        scope: auth0Config.scope
      }}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

// Custom Auth Provider that uses Auth0's hooks
const AuthProvider = ({ children }) => {
  const { 
    isAuthenticated, 
    isLoading, 
    user, 
    loginWithRedirect, 
    logout: auth0Logout,
    getAccessTokenSilently 
  } = useAuth0();
  
  // Custom login function
  const login = () => {
    return loginWithRedirect();
  };

  // Custom logout function
  const logout = () => {
    return auth0Logout({ 
      returnTo: window.location.origin 
    });
  };

  // Get access token function
  const getToken = async () => {
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      console.error("Failed to get access token", error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      user,
      login,
      logout,
      getToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use authentication context
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Layout wrappers
const MainLayout = ({ children }) => {
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);

  const handleOpenMashoAI = () => {
    setIsAIModalOpen(true);
  };

  const handleCloseMashoAI = () => {
    setIsAIModalOpen(false);
  };

  return (
    <>
      <Navbar />
      <PopupCard />
      <main>{children}</main>
      <Footer />
      
      {/* Floating AI Button */}
      {/* <MashoAIPop onOpen={handleOpenMashoAI} /> */}
      
      {/* AI Modal */}
      <MashoAIModal isOpen={isAIModalOpen} onClose={handleCloseMashoAI} />
    </>
  );
};

const AuthLayout = ({ children }) => (
  <>
    <main>{children}</main>
  </>
);

// Protected route wrapper
const ProtectedRoute = ({ element }) => {
  const { isAuthenticated } = useAuth();
  
  // Authentication check is still performed here
  // You can add logic to handle unauthenticated users if needed
  useEffect(() => {
    if (!isAuthenticated) {
      console.log("User is not authenticated");
      // Add any logic needed for unauthenticated users
    }
  }, [isAuthenticated]);
  
  return element;
};

function App() {
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [maintenanceData, setMaintenanceData] = useState({
    maintenance_message: "We are currently performing scheduled maintenance. We should be back online shortly.",
    estimated_time: "1 hour"
  });
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const checkMaintenanceMode = async () => {
      if (typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap") {
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/maintainance-mode/`);
        const data = response.data;
        
        if (data.is_maintenance) {
          setIsMaintenance(true);
          setMaintenanceData({
            maintenance_message: data.maintenance_message,
            estimated_time: data.estimated_time
          });
        }
      } catch (error) {
        console.error("Error checking maintenance mode:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkMaintenanceMode();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-center">
          {/* Blinking Masho Logo */}
          <div className="mb-6">
            <img 
              src={Mashologo} 
              alt="Masho Logo" 
              className="h-1/4 w-1/4 mx-auto"
              style={{
                animation: 'blink 1.5s ease-in-out infinite'
              }}
            />
          </div>
          
          {/* Golden Yellow Loading Line */}
          <div className="w-64 h-1 bg-gray-200 rounded-full mx-auto mb-4 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full"
              style={{
                animation: 'loadingLine 2s ease-in-out infinite'
              }}
            ></div>
          </div>
          
          <p className="text-gray-700 font-medium">Loading application...</p>
        </div>
        
        <style jsx>{`
          @keyframes loadingLine {
            0% { width: 0%; transform: translateX(-100%); }
            50% { width: 100%; transform: translateX(0%); }
            100% { width: 0%; transform: translateX(100%); }
          }
        `}</style>
      </div>
    );
  }

  if (isMaintenance) {
    return (
      <MaintenancePage 
        message={maintenanceData.maintenance_message} 
        estimatedTime={maintenanceData.estimated_time} 
      />
    );
  }

  return (
    <AuthProvider>
      <ScrollToTop/>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/masho-studio" element={<MainLayout><MashoStudioPage /></MainLayout>} />
        <Route path="/shop" element={<MainLayout><ShoppingPage /></MainLayout>} />
        <Route path="/combo-gifts" element={<MainLayout><ComboAndGifts /></MainLayout>} />
        <Route path="/combo-gifts/:id" element={<MainLayout><ComboAndGiftsInfo /></MainLayout>} />
        <Route path="/perfumes" element={<MainLayout><PerfumesList /></MainLayout>} />
        <Route path="/attars" element={<MainLayout><AttarList /></MainLayout>} />
        <Route path="/about" element={<MainLayout><AboutUs /></MainLayout>} />
        <Route path="/tracking" element={<MainLayout><TrackOrder /></MainLayout>} />
        <Route path="/product/:id" element={<MainLayout><ProductDetail /></MainLayout>} />
        <Route path="/new-arrivals" element={<MainLayout><NewArrivalPage /></MainLayout>} />
        <Route path="/testimonials" element={<MainLayout><TestimonialPage /></MainLayout>} />
        <Route path="/return-policy" element={<MainLayout><ReturnPolicy /></MainLayout>} />
        <Route path="/refund-policy" element={<MainLayout><RefundPolicy /></MainLayout>} />
        <Route path="/shipping-policy" element={<MainLayout><ShippingPolicy /></MainLayout>} />
        <Route path="/privacy-policy" element={<MainLayout><PrivacyPolicy /></MainLayout>} />
        <Route path="/terms-and-conditions" element={<MainLayout><TermsAndConditions /></MainLayout>} />
        <Route path="/logoutt" element={<MainLayout><LogoutButton /></MainLayout>} />
        <Route path="/signup" element={<AuthLayout><SignUp /></AuthLayout>} />
        <Route path="/payment-callback" element={<PaymentCallback />} />
        
        {/* Protected routes - authentication check still happens */}
        <Route 
          path="/user-details" 
          element={<MainLayout><ProtectedRoute element={<UserDetails />} /></MainLayout>} 
        />
        <Route 
          path="/profile" 
          element={<MainLayout><UserProfileSection /></MainLayout>} 
        />
        <Route 
          path="/cart" 
          element={<MainLayout><ProtectedRoute element={<CartPage />} /></MainLayout>} 
        />
        <Route 
          path="/billing" 
          element={<MainLayout><ProtectedRoute element={<BillingPage />} /></MainLayout>} 
        />
        <Route 
          path="/success" 
          element={<MainLayout><ProtectedRoute element={<OrderSuccess />} /></MainLayout>} 
        />
        <Route 
          path="/order-failure" 
          element={<MainLayout><ProtectedRoute element={<OrderFailure />} /></MainLayout>} 
        />

        {/* Fallback route */}
        <Route path="*" element={
          <MainLayout>
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
              <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
              <p className="text-xl text-gray-600 mb-6">Page Not Found</p>
              <a 
                href="/" 
                className="px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
              >
                Return to Home
              </a>
            </div>
          </MainLayout>
        } />
      </Routes>
    </AuthProvider>
  );
}

export default App;